import type { EventToken } from '@microsoft/wv2-interop';
import type {
    LegacyApp,
    ToggleOptions,
    ToggleProgressState,
    ImportProperties,
} from '@microsoft/wv2-outlook';
import {
    lazyGetGetToggledOutlooks,
    lazyGetGetOptions,
    lazyGetGetImportProperties,
    lazyGetResetBootAttempts,
    lazyGetLaunchLegacyApp,
    lazyGetLogToDisk,
    lazyGetGetOptionSettings,
    lazyGetSetOptionComplete,
    lazyGetSetToggleProgress,
    lazyGetAddProcessImportOptionsEventHandler,
    lazyGetRemoveProcessImportOptionsEventHandler,
} from './lazyFunctions';

// Types — zero runtime cost
/* eslint-disable owa-custom-rules/forbid-package-reexport  -- (https://aka.ms/OWALintWiki)
 * Re-exporting types from @microsoft/wv2-outlook so consumers don't need a direct dependency. */
export type {
    LegacyApp,
    ToggleOptions,
    ToggleProgressState,
    ImportProperties,
    NewOutlookToggleState,
} from '@microsoft/wv2-outlook';
/* eslint-enable owa-custom-rules/forbid-package-reexport */

// Constants — duplicated to avoid pulling in side-effectful gen module

// LegacyApp
export const LEGACY_APP_OUTLOOK_DESKTOP = 'OutlookDesktop' as const;
export const LEGACY_APP_UNIVERSAL = 'Universal' as const;

// ToggleOptions
export const TOGGLE_OPTIONS_REMOVE_ALL_ACCOUNTS = 'RemoveAllAccounts' as const;
export const TOGGLE_OPTIONS_IMPORT_ACCOUNTS = 'ImportAccounts' as const;
export const TOGGLE_OPTIONS_IMPORT_SETTINGS = 'ImportSettings' as const;

// ToggleProgressState
export const TOGGLE_PROGRESS_STATE_ABORT_ON_ERROR = 'AbortOnError' as const;
export const TOGGLE_PROGRESS_STATE_ABORT_ON_GO_BACK_CLICKED = 'AbortOnGoBackClicked' as const;
export const TOGGLE_PROGRESS_STATE_TOGGLED_IN = 'ToggledIn' as const;
export const TOGGLE_PROGRESS_STATE_TOGGLED_OUT = 'ToggledOut' as const;

// --- getToggledOutlooks ---

async function getGetToggledOutlooks(): Promise<(() => Promise<LegacyApp[]>) | undefined> {
    return lazyGetGetToggledOutlooks.importAndExecute();
}

export async function canCallGetToggledOutlooks(): Promise<boolean> {
    return (await getGetToggledOutlooks()) !== undefined;
}

export async function getToggledOutlooks(): Promise<LegacyApp[]> {
    const fn = await getGetToggledOutlooks();
    if (!fn) {
        throw new Error('getToggledOutlooks interop API is not available');
    }
    return fn();
}

// --- getOptions ---

async function getGetOptions(): Promise<(() => Promise<ToggleOptions[]>) | undefined> {
    return lazyGetGetOptions.importAndExecute();
}

export async function canCallGetOptions(): Promise<boolean> {
    return (await getGetOptions()) !== undefined;
}

export async function getOptions(): Promise<ToggleOptions[]> {
    const fn = await getGetOptions();
    if (!fn) {
        throw new Error('getOptions interop API is not available');
    }
    return fn();
}

// --- getImportProperties ---

async function getGetImportProperties(): Promise<(() => Promise<ImportProperties>) | undefined> {
    return lazyGetGetImportProperties.importAndExecute();
}

export async function canCallGetImportProperties(): Promise<boolean> {
    return (await getGetImportProperties()) !== undefined;
}

export async function getImportProperties(): Promise<ImportProperties> {
    const fn = await getGetImportProperties();
    if (!fn) {
        throw new Error('getImportProperties interop API is not available');
    }
    return fn();
}

// --- resetBootAttempts ---

async function getResetBootAttempts(): Promise<(() => Promise<void>) | undefined> {
    return lazyGetResetBootAttempts.importAndExecute();
}

export async function canCallResetBootAttempts(): Promise<boolean> {
    return (await getResetBootAttempts()) !== undefined;
}

export async function resetBootAttempts(): Promise<void> {
    const fn = await getResetBootAttempts();
    if (!fn) {
        throw new Error('resetBootAttempts interop API is not available');
    }
    return fn();
}

// --- launchLegacyApp ---

async function getLaunchLegacyApp(): Promise<
    | ((legacyApp: LegacyApp | undefined, sideBySideOnce: boolean | undefined) => Promise<void>)
    | undefined
> {
    return lazyGetLaunchLegacyApp.importAndExecute();
}

export async function canCallLaunchLegacyApp(): Promise<boolean> {
    return (await getLaunchLegacyApp()) !== undefined;
}

export async function launchLegacyApp(
    legacyApp: LegacyApp | undefined,
    sideBySideOnce: boolean | undefined
): Promise<void> {
    const fn = await getLaunchLegacyApp();
    if (!fn) {
        throw new Error('launchLegacyApp interop API is not available');
    }
    return fn(legacyApp, sideBySideOnce);
}

// --- logToDisk ---

async function getLogToDisk(): Promise<((content: string) => Promise<void>) | undefined> {
    return lazyGetLogToDisk.importAndExecute();
}

export async function canCallLogToDisk(): Promise<boolean> {
    return (await getLogToDisk()) !== undefined;
}

export async function logToDisk(content: string): Promise<void> {
    const fn = await getLogToDisk();
    if (!fn) {
        throw new Error('logToDisk interop API is not available');
    }
    return fn(content);
}

// --- getOptionSettings ---

async function getGetOptionSettings(): Promise<
    ((option: ToggleOptions) => Promise<string>) | undefined
> {
    return lazyGetGetOptionSettings.importAndExecute();
}

export async function canCallGetOptionSettings(): Promise<boolean> {
    return (await getGetOptionSettings()) !== undefined;
}

export async function getOptionSettings(option: ToggleOptions): Promise<string> {
    const fn = await getGetOptionSettings();
    if (!fn) {
        throw new Error('getOptionSettings interop API is not available');
    }
    return fn(option);
}

// --- setOptionComplete ---

async function getSetOptionComplete(): Promise<
    ((option: ToggleOptions) => Promise<void>) | undefined
> {
    return lazyGetSetOptionComplete.importAndExecute();
}

export async function canCallSetOptionComplete(): Promise<boolean> {
    return (await getSetOptionComplete()) !== undefined;
}

export async function setOptionComplete(option: ToggleOptions): Promise<void> {
    const fn = await getSetOptionComplete();
    if (!fn) {
        throw new Error('setOptionComplete interop API is not available');
    }
    return fn(option);
}

// --- setToggleProgress ---

async function getSetToggleProgress(): Promise<
    ((state: ToggleProgressState, toLegacyApp: LegacyApp | undefined) => Promise<void>) | undefined
> {
    return lazyGetSetToggleProgress.importAndExecute();
}

export async function canCallSetToggleProgress(): Promise<boolean> {
    return (await getSetToggleProgress()) !== undefined;
}

export async function setToggleProgress(
    state: ToggleProgressState,
    toLegacyApp: LegacyApp | undefined
): Promise<void> {
    const fn = await getSetToggleProgress();
    if (!fn) {
        throw new Error('setToggleProgress interop API is not available');
    }
    return fn(state, toLegacyApp);
}

// --- addProcessImportOptionsEventHandler ---

async function getAddProcessImportOptionsEventHandler(): Promise<
    ((eventHandler: () => Promise<void>) => EventToken) | undefined
> {
    return lazyGetAddProcessImportOptionsEventHandler.importAndExecute();
}

export async function canCallAddProcessImportOptionsEventHandler(): Promise<boolean> {
    return (await getAddProcessImportOptionsEventHandler()) !== undefined;
}

export async function addProcessImportOptionsEventHandler(
    eventHandler: () => Promise<void>
): Promise<EventToken> {
    const fn = await getAddProcessImportOptionsEventHandler();
    if (!fn) {
        throw new Error('addProcessImportOptionsEventHandler interop API is not available');
    }
    return fn(eventHandler);
}

// --- removeProcessImportOptionsEventHandler ---

async function getRemoveProcessImportOptionsEventHandler(): Promise<
    ((eventToken: EventToken) => void) | undefined
> {
    return lazyGetRemoveProcessImportOptionsEventHandler.importAndExecute();
}

export async function canCallRemoveProcessImportOptionsEventHandler(): Promise<boolean> {
    return (await getRemoveProcessImportOptionsEventHandler()) !== undefined;
}

export async function removeProcessImportOptionsEventHandler(
    eventToken: EventToken
): Promise<void> {
    const fn = await getRemoveProcessImportOptionsEventHandler();
    if (!fn) {
        throw new Error('removeProcessImportOptionsEventHandler interop API is not available');
    }
    fn(eventToken);
}
